import React, { useRef, useState } from "react";

/* ---------------- TYPES ---------------- */

export interface VennNode {
    id: string;
    name: string;
    value: number; // overlap %
    color: string;
}

type TooltipPayload =
    | {
        type: "primary";
        primary: VennNode;
    }
    | {
        type: "node";
        node: VennNode;
    }
    | {
        type: "intersection";
        primary: VennNode;
        node: VennNode;
        overlap: number;
    };

export type TooltipContext = TooltipPayload & {
    mouseX: number;
    mouseY: number;
};

interface Props {
    primaryNode: VennNode;
    nodes: VennNode[];
    tooltipRenderer?: (ctx: TooltipContext) => React.ReactNode;
}

/* ---------------- COMPONENT ---------------- */

const VennChart: React.FC<Props> = ({
    primaryNode,
    nodes,
    tooltipRenderer
}) => {
    const SIZE = 360;
    const HEIGHT = 360;
    const WIDTH = 420;
    const CENTER_X = WIDTH / 2;
    const CENTER_Y = HEIGHT / 2;
    const CENTER_RADIUS = 120;

    const [tooltipCtx, setTooltipCtx] = useState<TooltipContext | null>(null);

    // debounce refs
    const moveTimeoutRef = useRef<number | null>(null);
    const lastPayloadRef = useRef<TooltipPayload | null>(null);
    const lastMouseRef = useRef<{ x: number; y: number } | null>(null);

    /* ---------------- HANDLERS ---------------- */

    // Immediate on enter
    const handleEnter = (
        e: React.MouseEvent,
        payload: TooltipPayload
    ) => {
        const ctx: TooltipContext = {
            ...payload,
            mouseX: e.clientX,
            mouseY: e.clientY
        };

        lastPayloadRef.current = payload;
        lastMouseRef.current = { x: e.clientX, y: e.clientY };

        setTooltipCtx(ctx);
    };

    // Debounced mouse move
    const handleMove = (e: React.MouseEvent) => {
        if (!lastPayloadRef.current) return;

        lastMouseRef.current = { x: e.clientX, y: e.clientY };

        if (moveTimeoutRef.current !== null) return;

        moveTimeoutRef.current = window.setTimeout(() => {
            moveTimeoutRef.current = null;

            if (!lastPayloadRef.current || !lastMouseRef.current) return;

            setTooltipCtx({
                ...lastPayloadRef.current,
                mouseX: lastMouseRef.current.x,
                mouseY: lastMouseRef.current.y
            });
        }, 20);
    };

    // Cleanup
    const handleLeave = () => {
        if (moveTimeoutRef.current !== null) {
            clearTimeout(moveTimeoutRef.current);
            moveTimeoutRef.current = null;
        }
        lastPayloadRef.current = null;
        lastMouseRef.current = null;
        setTooltipCtx(null);
    };

    /* ---------------- RENDER ---------------- */

    return (
        <div style={{ position: "relative", width: WIDTH, height: HEIGHT }}>
            {/* TOOLTIP (follows mouse, debounced) */}
            {tooltipRenderer && tooltipCtx && (
                <div
                    style={{
                        position: "fixed",
                        left: tooltipCtx.mouseX + 12,
                        top: tooltipCtx.mouseY + 12,
                        pointerEvents: "none",
                        zIndex: 9999
                    }}
                >
                    {tooltipRenderer(tooltipCtx)}
                </div>
            )}

            <svg width={WIDTH} height={HEIGHT}>
                {/* DEFINITIONS */}
                <defs>
                    {/* Intersection clip (primary circle) */}
                    {nodes.map(n => (
                        <clipPath key={`clip-${n.id}`} id={`clip-${n.id}`}>
                            <circle
                                cx={CENTER_X}
                                cy={CENTER_Y}
                                r={CENTER_RADIUS}
                            />
                        </clipPath>
                    ))}

                    {/* Node-only mask (subtract primary overlap) */}
                    {nodes.map(n => (
                        <mask key={`mask-${n.id}`} id={`mask-${n.id}`}>
                            <rect width="100%" height="100%" fill="white" />
                            <circle
                                cx={CENTER_X}
                                cy={CENTER_Y}
                                r={CENTER_RADIUS}
                                fill="black"
                            />
                        </mask>
                    ))}
                </defs>

                {/* PRIMARY CIRCLE */}
                <circle
                    cx={CENTER_X}
                    cy={CENTER_Y}
                    r={CENTER_RADIUS}
                    fill={primaryNode.color}
                    opacity={0.35}
                    onMouseEnter={e =>
                        handleEnter(e, {
                            type: "primary",
                            primary: primaryNode
                        })
                    }
                    onMouseMove={handleMove}
                    onMouseLeave={handleLeave}
                />

                <text
                    x={CENTER_X}
                    y={CENTER_Y}
                    textAnchor="middle"
                    dy="4"
                    fontSize={13}
                    fontWeight={700}
                    fill="#3E4C59"
                    pointerEvents="none"
                >
                    {primaryNode.name}
                </text>

                {/* COMPETITOR CIRCLES */}
                {nodes.map((n, i) => {
                    const angle = (2 * Math.PI * i) / nodes.length;
                    const r = 30 + n.value * 1.2;

                    const hasOverlap = n.value > 0;

                    const minOverlap = hasOverlap ? 14 : 0;

                    const maxDistance = CENTER_RADIUS + r - minOverlap;
                    const minDistance = CENTER_RADIUS * 0.35;

                    // if no overlap, push circle fully outside
                    const distance = hasOverlap
                        ? Math.max(
                            minDistance,
                            maxDistance * (1 - n.value / 100)
                        )
                        : CENTER_RADIUS + r + 8;


                    const cx = CENTER_X + Math.cos(angle) * distance;
                    const cy = CENTER_Y + Math.sin(angle) * distance;

                    return (
                        <g key={n.id}>
                            {/* NODE ONLY (paint first) */}
                            <circle
                                cx={cx}
                                cy={cy}
                                r={r}
                                mask={`url(#mask-${n.id})`}
                                fill="transparent"
                                onMouseEnter={e =>
                                    handleEnter(e, {
                                        type: "node",
                                        node: n
                                    })
                                }
                                onMouseMove={handleMove}
                                onMouseLeave={handleLeave}
                            />

                            {/* INTERSECTION (paint last) */}
                            <circle
                                cx={cx}
                                cy={cy}
                                r={r}
                                clipPath={`url(#clip-${n.id})`}
                                fill="transparent"
                                onMouseEnter={e =>
                                    handleEnter(e, {
                                        type: "intersection",
                                        primary: primaryNode,
                                        node: n,
                                        overlap: n.value
                                    })
                                }
                                onMouseMove={handleMove}
                                onMouseLeave={handleLeave}
                            />

                            {/* VISUAL CIRCLE */}
                            <circle
                                cx={cx}
                                cy={cy}
                                r={r}
                                fill={n.color}
                                opacity={0.35}
                                pointerEvents="none"
                            />

                            <text
                                x={cx}
                                y={cy}
                                textAnchor="middle"
                                dy="4"
                                fontSize={12}
                                fontWeight={700}
                                fill="#3E4C59"
                                pointerEvents="none"
                            >
                                {n.name}
                            </text>
                        </g>
                    );
                })}
            </svg>
        </div>
    );
};

export default VennChart;
